import type { GetNotificationsResponse } from "@backend-ctrl/notification.controller";
import { Box, Button, Divider, IconButton, Menu, Stack, Typography } from "@mui/material";
import {
  Close,
  Comment,
  NotificationImportant,
  Notifications as NotificationIcon,
  NotificationsOutlined,
  Subscriptions,
  ThumbUp,
  Tv,
} from "@mui/icons-material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { notificationsSeen, useNotifications, useUpdateNotification, useUserState } from "../../services/backend";
import { generatePath, useHistory } from "react-router-dom";
import useNotificationMenuStyles from "./NotificationMenu.styles";
import { captureAndDisplayError, checkIsTouchDevice, displayMessage, displayUndo } from "../../utils";
import { NotificationTypeEnum } from "@weepy/shared";
import { links } from "../../constants";

import type { OverridableComponent } from "@mui/material/OverridableComponent";
import type { SvgIconTypeMap } from "@mui/material/SvgIcon";
import DayJS from "../DayJS";
import type { History } from "history";
import { motion, useAnimation, PanInfo } from "framer-motion";
import Announcements from "./Announcements";

const iconMap: Partial<Record<NotificationTypeEnum, OverridableComponent<SvgIconTypeMap<{}, "svg">>>> = {
  [NotificationTypeEnum.COMMENT_LIKE]: ThumbUp,
  [NotificationTypeEnum.COMMENT_REPLY]: Comment,
  [NotificationTypeEnum.LIBRARY_ITEM_UPDATED]: Subscriptions,
  [NotificationTypeEnum.LIBRARY_UPDATED]: Tv,
};

export const NotificationMenu: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
  const classes = useNotificationMenuStyles();
  const [menuOpen, setMenuOpen] = useState(false);

  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const { data: userState } = useUserState();
  const [viewing, setViewing] = useState<"personal" | "announces">("personal");
  const nc = userState?.notification_unseen_count || 0;
  const ac = userState?.announcement_unseen_count || 0;

  // set notifications as seen when menu opens
  useEffect(() => {
    if (!menuOpen) return;
    const id = setTimeout(() => {
      notificationsSeen().catch((e) => captureAndDisplayError(e, false));
    }, 1000);
    return () => clearTimeout(id);
  }, [menuOpen]);

  // On open, change viewing to announces (from default personal) if its unseen_count is > 0
  useEffect(() => {
    if (!menuOpen) return;
    if (nc === 0 && ac > 0) setViewing("announces");
  }, [menuOpen, nc, ac]);

  const handleClose = () => setMenuOpen(false);
  const handleOpen = () => setMenuOpen(true);

  const unseen_count = useMemo(() => ac + nc, [ac, nc]);

  return (
    <div style={style}>
      {/* Notificatons Button */}
      <IconButton
        aria-label={"Open notifications"}
        size="small"
        onClick={handleOpen}
        ref={menuButtonRef}
        style={{ color: "white" }}
      >
        {unseen_count && unseen_count > 0 ? (
          <>
            <NotificationImportant />
            <div style={{ position: "relative" }}>
              <span className={classes.count}>{unseen_count}</span>
            </div>
          </>
        ) : menuOpen ? (
          <NotificationIcon />
        ) : (
          <NotificationsOutlined />
        )}
      </IconButton>

      <Menu
        anchorEl={menuButtonRef.current}
        keepMounted // to remember scroll pos.
        // getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={menuOpen}
        onClose={handleClose}
        MenuListProps={{ disablePadding: true }}
        classes={{ paper: classes.menuPaper }}
      >
        {/* empty elm fixes weird forward ref warning */}
        <span></span>
        <NotificationHeader handleClose={handleClose} />

        <Stack
          direction={"row"}
          justifyContent="center"
          sx={{ "> *": { textAlign: "center", width: "100%", borderRadius: "unset !important" } }}
        >
          <Button
            size="small"
            color={viewing === "announces" ? "inherit" : undefined}
            sx={{ borderBottom: viewing === "personal" ? (t) => `1px solid ${t.palette.primary.main}` : undefined }}
            onClick={() => setViewing("personal")}
          >
            Personal {nc ? `(${nc})` : ""}
          </Button>

          <Button
            size="small"
            color={viewing === "personal" ? "inherit" : undefined}
            sx={{ borderBottom: viewing === "announces" ? (t) => `1px solid ${t.palette.primary.main}` : undefined }}
            onClick={() => setViewing("announces")}
          >
            Announcements {ac ? `(${ac})` : ""}
          </Button>
        </Stack>

        <div style={{ overflowY: "auto", overflowX: "hidden", maxHeight: "80vh" }}>
          {viewing === "personal" ? (
            <Personal menuOpen={menuOpen} handleClose={handleClose} />
          ) : (
            <Announcements menuOpen={menuOpen} handleClose={handleClose} />
          )}
        </div>
      </Menu>
    </div>
  );
};

type ViewProps = {
  menuOpen: boolean;
  handleClose: () => void;
};
const Personal = ({ menuOpen, handleClose }: ViewProps) => {
  const { data: notifications, error: errLoadingNotifs } = useNotifications({ enabled: menuOpen });
  const { error: errLoadingState } = useUserState();
  const history = useHistory();

  return (
    <>
      {errLoadingNotifs || errLoadingState ? (
        <Box textAlign={"center"} color="red">
          {`Error: ${errLoadingNotifs?.message || errLoadingState?.message}`}
        </Box>
      ) : !notifications ? (
        <Box textAlign={"center"} color="#eee" width={330}>
          {/* width=300: Give the menu paper some width if no notifications loaded in yet.  */}
          {/* This helps the menu anchor to position the menu in a better place when they're /first/ loaded (not cached).  */}
          {"Loading..."}
        </Box>
      ) : notifications.length === 0 ? (
        <Typography align="center" style={{ color: "#eee" }}>
          {"None right now"}
        </Typography>
      ) : (
        notifications.map((notification) => (
          <div key={`NOTIFICATION_${notification.id}`}>
            <NotificationItem history={history} onItemClick={handleClose} notification={notification} />
          </div>
        ))
      )}
    </>
  );
};

interface HeaderProps {
  handleClose: () => void;
}
const NotificationHeader: React.FC<HeaderProps> = ({ handleClose }) => {
  const classes = useNotificationMenuStyles();
  return (
    <div className={classes.headerDiv}>
      <Typography component="div" className={classes.headerText}>
        <NotificationIcon style={{ position: "relative", top: 2 }} fontSize="small" /> Notifications
      </Typography>
      <IconButton className={classes.closeButton} onClick={handleClose} size="large">
        <Close />
      </IconButton>
    </div>
  );
};

interface NotificationItemProps {
  onItemClick: () => void;
  history: History;
  notification: GetNotificationsResponse[1];
}
const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onItemClick, history }) => {
  const classes = useNotificationMenuStyles();
  const controls = useAnimation();
  const [lastDragged, setLastDragged] = useState<null | number>(null);
  // const [hovering, setHovering] = useState(false);

  const { type } = notification;
  let link = "#";

  const mutation = useUpdateNotification();
  const hideNotification = async () => {
    const { data: r } = await mutation.mutateAsync({ id: notification.id, hidden: true });
    if (r && r.message && r.undo_id) {
      displayUndo({ message: r.message, undo_id: r.undo_id });
    }
  };

  let innerElement: JSX.Element = (
    <div>
      {/* <div className={classes.notificationItemHeaderContainer}>
        <span>{`${notification.type}`}</span>
      </div> */}
      {`${notification.message}`}
    </div>
  );

  if ("comment" in notification) {
    const comment = notification.comment;
    // const commentPreview = `${comment?.text.slice(0, 90)}${comment && comment.text.length > 50 ? "...-" : ""}`;
    // If spoiler but notification type is comment like, don't hide it (the user created this comment, so it's fine if they see it.) *
    const commentText = comment?.is_spoiler && type !== "COMMENT_LIKE" ? "[Spoiler, click to open]" : comment?.text;
    link = generatePath(links.library_item.path, { id: comment?.library_map_id! });

    const MaybeIcon = iconMap[notification.type];
    innerElement = (
      <div>
        <div className={classes.notificationItemHeaderContainer}>
          {MaybeIcon && <MaybeIcon style={{ fontSize: 16 }} />}
          <span>&nbsp;{`${notification.message}`}</span>
        </div>
        <div className={classes.commentText}>{commentText}</div>
      </div>
    );
  } else if ("library_item_id" in notification) {
    link = generatePath(links.library_item.path, { id: notification.library_item_id });
    const MaybeIcon = iconMap[notification.type];
    innerElement = (
      <div>
        <div className={classes.notificationItemHeaderContainer}>
          {MaybeIcon && <MaybeIcon style={{ fontSize: 16 }} />}
          <span>&nbsp;{`Subscription Update`}</span>
        </div>
        <div>{`${notification.message}`}</div>
      </div>
    );
  }
  //  else if ("library_id" in notification) {
  // TODO: test this .. library_id might exist in another type, and in that case we wouldn't watn link to that library
  // should use enums ..
  // link = generatePath(links.folder, {folder: notification.library.http_path_name })
  // }

  const _itemClicked = () => {
    if (lastDragged && Date.now() - lastDragged < 1000) return;
    if (window.location.pathname === link) {
      displayMessage("You're already on this page.", "info");
      return onItemClick(); // close the menu
      // return window.location.reload();
    }

    onItemClick();
    history.push(link);
  };

  async function handleDragEnd(event: any, info: PanInfo) {
    const offsetX = info.offset.x;
    const velocity = info.velocity.x;

    if (Math.abs(velocity) > 10 && (offsetX < -100 || velocity < -500)) {
      await controls.start({ x: "-100%", transition: { duration: 0.2 } });
      hideNotification();
    } else {
      controls.start({ x: 0, opacity: 1, transition: { duration: 0.5 } });
    }
    setLastDragged(Date.now());
  }

  return (
    <motion.div style={{ position: "relative" }} layout transition={{ type: "spring", stiffness: 600, damping: 30 }}>
      <motion.div
        drag="x"
        dragElastic={{ right: 0.1, left: 0.5 }}
        dragConstraints={{ right: 0, left: 0 }}
        dragDirectionLock
        onDragStart={() => setLastDragged(Date.now())}
        onDragEnd={handleDragEnd}
        animate={controls}
      >
        {/* <NavLink to={link} style={{ textDecoration: "none" }}> */}
        <div onClick={_itemClicked} className={classes.menuItem}>
          {/* Text */}
          {innerElement}
          {/* Notificaiton Age */}
          <div className={classes.notifAge}>
            <DayJS date={notification.created_at} fromNow />
          </div>
        </div>
        {/* </NavLink> */}
      </motion.div>

      {/* Hide notification btn */}
      {!checkIsTouchDevice() && (
        <IconButton onClick={hideNotification} className={classes.markHiddenBtn} size="large">
          <Close style={{ fontSize: 18 }} />
        </IconButton>
      )}

      <Divider />
    </motion.div>
  );
};
